// src\themes\default\components\NewsGrid.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

type NewsGridItem = {
    tag?: string;
    title?: string;
    date?: string;
    readTime?: string;
    link?: string;
    image?: { url: string; description?: string };
};

type NewsGridData = {
    title?: string;
    description?: string;
    items?: NewsGridItem[];
};

export default function NewsGrid({ data }: { data: NewsGridData }) {
    const items = data?.items || [];

    if (items.length === 0) return null;

    return (
        <div className="col-span-12">
            {(data.title || data.description) && (
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                    variants={textGroupVariants}
                    className="mb-6 lg:mb-8 space-y-1.5"
                >
                    {data.title && (
                        <motion.h2
                            variants={textItemVariants}
                            className="text-xl lg:text-3xl font-light text-primary-900 tracking-tight"
                        >
                            {data.title}
                        </motion.h2>
                    )}
                    {data.description && (
                        <motion.p variants={textItemVariants} className="text-[13px] text-primary-500">
                            {data.description}
                        </motion.p>
                    )}
                </motion.div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 lg:gap-x-8 lg:gap-y-12">
                {items.map((item, index) => {
                    const Content = (
                        <div className="group/card flex flex-col h-full">
                            <div className="relative w-full aspect-[16/11] overflow-hidden rounded-2xl bg-primary-100">
                                {item.image?.url && (
                                    <Image
                                        src={item.image.url}
                                        alt={item.image.description || item.title || ''}
                                        fill
                                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                        className="object-cover transition-transform duration-[900ms] ease-out group-hover/card:scale-[1.035]"
                                    />
                                )}
                                {item.tag && (
                                    <span className="absolute top-4 ltr:left-4 rtl:right-4 inline-flex items-center rounded-full bg-primary-0/90 backdrop-blur px-3 py-1 text-[11px] text-primary-900 tracking-wide">
                                        {item.tag}
                                    </span>
                                )}
                            </div>

                            <div className="mt-4 flex flex-col flex-1">
                                <h3 className="text-lg lg:text-xl font-light text-primary-900 leading-snug tracking-tight">
                                    {item.title}
                                </h3>

                                {(item.date || item.readTime) && (
                                    <div className="flex items-center justify-between mt-3 text-[11px] text-primary-400">
                                        <span>{item.date}</span>
                                        <span>{item.readTime}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );

                    return item.link ? (
                        <Link key={index} href={item.link}>{Content}</Link>
                    ) : (
                        <div key={index}>{Content}</div>
                    );
                })}
            </div>
        </div>
    );
}
